import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2, Globe, MapPin, Flame, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";
import { fmtEUR, MODALITY_LABELS } from "@/pages/cotizador/shared";
import { mapProductType } from "@/lib/serviceTypes";
import { SERVICE_TYPE_LABELS } from "@/lib/serviceTypes";

// Test Ventas 23-oct (punto 5): el destino de la cotización y el interruptor «Buscar en todos
// los destinos» se comparten entre todos los buscadores del editor vía contexto.
const CotSearchContext = createContext({ destination: "", allDestinations: false, setAllDestinations: () => {} });

export function CotSearchProvider({ destination, children }) {
  const [allDestinations, setAllDestinations] = useState(false);
  return <CotSearchContext.Provider value={{ destination: destination || "", allDestinations, setAllDestinations }}>{children}</CotSearchContext.Provider>;
}

const PAGE = 12;

// Buscador del catálogo de productos (It.3) con filtros de ciudad, tipo y destino.
// Orden (punto 2-3): título contiene lo buscado → más usadas por el equipo → resto.
// onPick recibe el producto del catálogo; onTextChange el texto manual.
export function CotCatalogSearch({ value, city, productType, pax, onTextChange, onPick, testid, placeholder }) {
  const { destination, allDestinations, setAllDestinations } = useContext(CotSearchContext);
  const [term, setTerm] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hl, setHl] = useState(0);
  const [limit, setLimit] = useState(PAGE);
  const [hidden, setHidden] = useState(() => new Set());
  const boxRef = useRef(null);
  const qc = useQueryClient();

  useEffect(() => { const t = setTimeout(() => { setQ(term.trim()); setLimit(PAGE); }, 220); return () => clearTimeout(t); }, [term]);
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Multi-ciudad por comas, «/» o «·» → consultas paralelas combinadas; ciudad vacía = sin filtro.
  // Con «Buscar en todos los destinos» activo la ciudad del día no limita la búsqueda.
  const cities = allDestinations ? [] : (city || "").split(/[,/·]/).map((c) => c.trim()).filter(Boolean);
  const enabled = open && (q.length >= 2 || cities.length > 0 || !!productType);
  const isLodging = productType === "alojamiento";
  const destParams = { destination: destination || "", all_destinations: allDestinations || !destination };
  const searchQ = useQuery({
    queryKey: ["cot-catalog", q, productType || "", city || "", pax || 1, destination, allDestinations, limit],
    enabled,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const base = { limit };
      if (q) base.q = q;
      if (productType) base.type = productType;
      const seen = new Set();
      const out = [];
      let hasMore = false;
      const absorb = (r) => { (r.data.items || []).forEach((p) => { if (!seen.has(p.id)) { seen.add(p.id); out.push(p); } }); if (r.data.has_more) hasMore = true; };

      // Caso especial: alojamientos → maestro KimKim de hoteles (sin precio).
      if (isLodging) {
        const lodgeCalls = cities.length === 0
          ? [api.get("/cotizador/kk-catalog/lodgings/search", { params: { q, limit, ...destParams } })]
          : cities.map((c) => api.get("/cotizador/kk-catalog/lodgings/search", { params: { q, city: c, limit, ...destParams } }));
        (await Promise.all(lodgeCalls)).forEach(absorb);
        // Fallback: si KimKim no encuentra ningún hotel, buscar en el catálogo legacy (con plantilla TP asociada).
        if (out.length === 0) {
          const legacyCalls = cities.length === 0
            ? [api.get("/products", { params: base })]
            : cities.map((c) => api.get("/products", { params: { ...base, city: c } }));
          (await Promise.all(legacyCalls)).forEach(absorb);
        }
        return { items: out, hasMore };
      }

      // 1) KimKim addons (actividades/transporte)
      const kkBase = { ...base, pax: pax || 1, ...destParams };
      const kkCalls = cities.length === 0
        ? [api.get("/cotizador/kk-catalog/search", { params: kkBase })]
        : cities.map((c) => api.get("/cotizador/kk-catalog/search", { params: { ...kkBase, city: c } }));
      (await Promise.all(kkCalls)).forEach(absorb);
      // El backend ya ordena cada llamada; al combinar varias ciudades re-aplicamos el criterio.
      const ql = q.toLowerCase();
      out.sort((a, b) => {
        const ga = ql && (a.name || "").toLowerCase().includes(ql) ? 0 : (a.usage_count > 0 ? 1 : 2);
        const gb = ql && (b.name || "").toLowerCase().includes(ql) ? 0 : (b.usage_count > 0 ? 1 : 2);
        return ga - gb || (b.usage_count || 0) - (a.usage_count || 0) || (a.name || "").localeCompare(b.name || "");
      });

      // 2) Fallback al catálogo legacy SOLO si KimKim no encuentra nada
      //    (el legacy sí trae plantilla TP asociada, que se aplicará al pickear).
      if (out.length === 0) {
        const legacyCalls = cities.length === 0
          ? [api.get("/products", { params: base })]
          : cities.map((c) => api.get("/products", { params: { ...base, city: c } }));
        (await Promise.all(legacyCalls)).forEach(absorb);
      }
      return { items: out, hasMore };
    },
  });
  const results = (searchQ.data?.items || []).filter((p) => !hidden.has(p.id));
  const hasMore = !!searchQ.data?.hasMore;
  const pick = (p) => { onPick(p); setOpen(false); };

  const deleteFromCatalog = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}" del catálogo? Es permanente.`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      setHidden((h) => new Set(h).add(p.id));
      qc.invalidateQueries({ queryKey: ["cot-catalog"] });
      toast.success("Eliminada del catálogo");
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const onKey = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHl((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHl((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (results[hl]) pick(results[hl]); }
    else if (e.key === "Escape") setOpen(false);
  };

  const destLabel = destination ? destination.split(/[·,/]/).map((x) => x.trim()).filter(Boolean).slice(0, 3).join(" · ") : "";
  const showDestination = allDestinations || !destination;

  return (
    <div className="relative" ref={boxRef}>
      <input
        data-testid={testid}
        value={value || ""}
        placeholder={placeholder || (city ? `Buscar en ${city}…` : "Buscar en el catálogo o escribir…")}
        className="h-8 w-full rounded-md border bg-background px-2 text-sm font-medium outline-none focus:ring-1 focus:ring-ring"
        onChange={(e) => { onTextChange(e.target.value); setTerm(e.target.value); setOpen(true); setHl(0); }}
        onFocus={() => { setTerm(value || ""); setOpen(true); }}
        onKeyDown={onKey}
      />
      {open && enabled && (
        <div className="absolute z-50 mt-1 flex max-h-[32rem] w-[36rem] max-w-[90vw] flex-col rounded-md border bg-popover shadow-lg" data-testid={`${testid}-dropdown`}>
          <div className="flex items-center justify-between gap-2 border-b px-2 py-1.5 text-[11px]">
            <span className="flex items-center gap-1 text-muted-foreground">
              {destination ? (<><MapPin className="h-3 w-3" /> {allDestinations ? "Todos los destinos" : `Destino del viaje: ${destLabel}`}</>) : (<><Globe className="h-3 w-3" /> Sin destino en la cotización · buscando en todo el catálogo</>)}
              {!q && !isLodging && <span className="ml-1 rounded bg-muted px-1 py-0.5 text-[9px] uppercase tracking-wide">más usadas</span>}
            </span>
            {destination && (
              <button type="button" onClick={() => { setAllDestinations(!allDestinations); setLimit(PAGE); }} data-testid={`${testid}-all-dest`}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors ${allDestinations ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                <Globe className="h-3 w-3" /> {allDestinations ? "Todos los destinos · ON" : "Buscar en todos los destinos"}
              </button>
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-1">
            {searchQ.isFetching && results.length === 0 && (
              <div className="flex items-center gap-2 px-2 py-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Buscando…</div>
            )}
            {!searchQ.isFetching && results.length === 0 && (
              <div className="px-2 py-2 text-xs text-muted-foreground">Sin resultados en el catálogo{destination && !allDestinations ? " de este destino. Prueba «Buscar en todos los destinos»." : "."}</div>
            )}
            {results.map((p, i) => {
              const isKK = p.source === "kimkim";
              const isKKLodging = p.source === "kimkim_lodging";
              const total = Number(p.cost_tax_incl) || Number(p.cost_tax_excl) || 0;
              const paxCovered = p.pax && p.pax > 0 ? p.pax : 1;
              const ppax = paxCovered > 1 ? total / paxCovered : total;
              const prevGroup = i > 0 ? results[i - 1].rank_group : null;
              const groupLabel = p.rank_group !== prevGroup && q ? ({ 0: "Coincide en el título", 1: "Más usadas por el equipo", 2: "Otros resultados" })[p.rank_group] : null;
              return (
                <React.Fragment key={p.id}>
                  {groupLabel && <div className="px-2 pb-0.5 pt-1.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground" data-testid={`${testid}-group-${p.rank_group}`}>{groupLabel}</div>}
                  <div className={`flex items-stretch rounded ${i === hl ? "bg-accent" : "hover:bg-accent"}`} onMouseEnter={() => setHl(i)}>
                    <button
                      type="button"
                      data-testid={`catalog-opt-${p.id}`}
                      onClick={() => pick(p)}
                      className="flex min-w-0 flex-1 items-start justify-between gap-3 px-2 py-1.5 text-left text-sm"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1">
                          {isKK && (
                            <span className="shrink-0 rounded bg-blue-600 px-1 py-0 text-[9px] font-bold uppercase text-white" title="Addon KimKim (precio ya limpio del 8 %)">KK</span>
                          )}
                          {isKKLodging && (
                            <span className="shrink-0 rounded bg-indigo-600 px-1 py-0 text-[9px] font-bold uppercase text-white" title="Alojamiento del maestro KimKim (sin precio)">HOTEL</span>
                          )}
                          {isKKLodging && p.is_apartment && (
                            <span className="shrink-0 rounded border border-amber-300 bg-amber-50 px-1 py-0 text-[9px] font-semibold text-amber-800" title="Apartamento familiar">APT</span>
                          )}
                          {isKKLodging && p.stars ? (
                            <span className="shrink-0 text-[10px] text-amber-500" title={`${p.stars} estrellas`}>{"★".repeat(p.stars)}</span>
                          ) : null}
                          <span className="block truncate font-medium">{p.name}</span>
                          {p.usage_count > 0 && (
                            <span className="inline-flex shrink-0 items-center gap-0.5 rounded bg-orange-100 px-1 py-0 text-[9px] font-semibold text-orange-700" title={`Añadida ${p.usage_count} veces en cotizaciones del equipo`} data-testid={`catalog-usage-${p.id}`}><Flame className="h-2.5 w-2.5" />{p.usage_count}</span>
                          )}
                        </span>
                        <span className="block truncate text-[11px] text-muted-foreground">
                          {isKKLodging
                            ? [p.city, showDestination ? p.destination : null, p.preference].filter(Boolean).join(" · ")
                            : ([p.provider_name, (p.cities && p.cities.length > 1 ? p.cities.slice(0, 3).join(" · ") : p.city), showDestination ? (p.destination || p.country) : null, p.duration, MODALITY_LABELS[p.modality]].filter(Boolean).join(" · ") || "—")}
                        </span>
                      </span>
                      <span className="shrink-0 text-right text-[11px] tabular-nums">
                        {isKKLodging ? (
                          <span className="text-[10px] italic text-muted-foreground">sin precio</span>
                        ) : (
                          <>
                            <span className="font-semibold">{fmtEUR(ppax)}<span className="font-normal text-muted-foreground"> /pax</span></span>
                            <span className="block text-[10px] text-muted-foreground">
                              total {fmtEUR(total)} · {paxCovered} pax
                            </span>
                            {isKK && p.kk_price_gross ? (
                              <span className="mt-0.5 inline-block rounded border border-blue-200 bg-blue-50 px-1 text-[9px] tracking-wide text-blue-700" title={`Precio original KimKim (con 8 % comisión). Origen: ${p.kk_price_source}${p.kk_pricing_display ? " · " + p.kk_pricing_display : ""}`}>
                                KK {fmtEUR(Number(p.kk_price_gross))}
                              </span>
                            ) : (
                              <span className="mt-0.5 inline-block rounded bg-muted px-1 text-[9px] uppercase tracking-wide text-muted-foreground">{SERVICE_TYPE_LABELS[mapProductType(p.type, p.name)] || p.type}</span>
                            )}
                          </>
                        )}
                      </span>
                    </button>
                    {!isKK && !isKKLodging && (
                      <button
                        type="button"
                        data-testid={`catalog-del-${p.id}`}
                        onClick={(e) => { e.stopPropagation(); deleteFromCatalog(p); }}
                        title="Eliminar del catálogo (permanente)"
                        className="flex items-center justify-center border-l px-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
          {hasMore && (
            <button type="button" onClick={() => setLimit((l) => l + PAGE * 2)} disabled={searchQ.isFetching} data-testid={`${testid}-more`}
              className="flex items-center justify-center gap-1 border-t px-2 py-1.5 text-xs font-medium text-primary hover:bg-muted disabled:opacity-60">
              {searchQ.isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ChevronDown className="h-3.5 w-3.5" />} Ver más resultados
            </button>
          )}
        </div>
      )}
    </div>
  );
}
